import React, { useEffect, useRef } from "react";
import { NavLink } from 'react-router-dom';
import Aos from "aos";
import "aos/dist/aos.css";
import "./Home.css";
import { FaCode, FaMobile, FaGamepad, FaDesktop, FaPencilRuler, FaChalkboardTeacher, FaAngleRight, FaRocket, 
         FaSearch, FaClipboardList, FaPaintBrush, FaTools, FaCheckCircle, FaPaperPlane, FaLaptopCode, FaShieldAlt, 
         FaBolt, FaUserFriends, FaCogs, FaRegLightbulb, FaStar, FaArrowRight } from 'react-icons/fa';

export default function Home() {
  const heroContentRef = useRef(null);
  const heroIllustrationRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    Aos.init({
      duration: 1000,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      once: false,
    });

    // Parallax effect for hero section
    const handleScroll = () => {
      if (heroContentRef.current && heroIllustrationRef.current) {
        const scrollY = window.scrollY;
        heroContentRef.current.style.transform = `translateY(${scrollY * 0.2}px)`;
        heroIllustrationRef.current.style.transform = `translateY(${scrollY * 0.1}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Animate stats on visibility
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          startCountAnimation();
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (statsRef.current) observer.unobserve(statsRef.current);
    };
  }, []);
  
  const startCountAnimation = () => {
    const statItems = document.querySelectorAll('.hero-stat-number');
    
    statItems.forEach(item => {
      const target = parseInt(item.getAttribute('data-target'));
      let count = 0;
      const duration = 2000; // ms
      const frameDuration = 1000 / 60; // 60fps
      const totalFrames = Math.round(duration / frameDuration);
      const increment = target / totalFrames;
      
      const counter = setInterval(() => {
        count += increment;
        
        if (count >= target) {
          item.textContent = target + '+';
          clearInterval(counter);
        } else {
          item.textContent = Math.floor(count) + '+';
        }
      }, frameDuration);
    });
  };

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-wave-top">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path fill="rgba(44, 60, 84, 0.05)" d="M0,160L48,154.7C96,149,192,139,288,149.3C384,160,480,192,576,197.3C672,203,768,181,864,170.7C960,160,1056,160,1152,176C1248,192,1344,224,1392,240L1440,256L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path>
          </svg>
        </div>

        <div className="animated-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
        </div>

        <div className="hero-content" ref={heroContentRef} data-aos="fade-up">
          <div className="hero-badge">Namibia's Premier Tech Company</div>
          <h1>Transforming <span className="highlight">Ideas</span> Into Digital <span className="highlight-2">Reality</span></h1>
          <p className="hero-subtitle">
            A leading Namibian technology company delivering innovative, high-quality 
            digital solutions tailored to your unique business needs.
          </p>
          <div className="hero-buttons">
            <NavLink to="/contact" className="btn-primary glow-effect">Get Started <FaAngleRight className="btn-icon" /></NavLink>
            <NavLink to="/services" className="btn-secondary">Explore Services</NavLink>
          </div>
          
          <div className="hero-stats" ref={statsRef}>
            <div className="hero-stat-item">
              <div className="hero-stat-number" data-target="100">0+</div>
              <div className="hero-stat-text">Projects Completed</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-number" data-target="50">0+</div>
              <div className="hero-stat-text">Happy Clients</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-number" data-target="3">0+</div>
              <div className="hero-stat-text">Years Experience</div>
            </div>
          </div>
        </div>
        
        <div className="hero-illustration" ref={heroIllustrationRef} data-aos="fade-left" data-aos-delay="200">
          <svg viewBox="0 0 600 400" className="main-svg">
            {/* Digital world illustration - abstract laptop and code with enhanced visibility */}
            <rect x="300" y="100" width="150" height="200" rx="12" fill="#2c3c5450" className="floating" />
            <rect x="330" y="130" width="90" height="10" rx="5" fill="#2c3c5470" className="floating-delayed" />
            <rect x="330" y="150" width="90" height="10" rx="5" fill="#2c3c5470" className="floating-delayed" />
            <rect x="330" y="170" width="60" height="10" rx="5" fill="#2c3c5470" className="floating-delayed" />
            <rect x="330" y="210" width="90" height="60" rx="5" fill="#2c3c5440" className="pulse" />
            
            <rect x="150" y="150" width="120" height="150" rx="12" fill="#2c3c5450" className="floating-reverse" />
            <rect x="170" y="170" width="80" height="8" rx="4" fill="#2c3c5470" className="floating-delayed-reverse" />
            <rect x="170" y="190" width="80" height="8" rx="4" fill="#2c3c5470" className="floating-delayed-reverse" />
            <rect x="170" y="210" width="50" height="8" rx="4" fill="#2c3c5470" className="floating-delayed-reverse" />
            <rect x="170" y="240" width="80" height="40" rx="5" fill="#2c3c5440" className="pulse-slow" />
            
            {/* Enhanced decorative elements */}
            <circle cx="390" cy="320" r="30" fill="#942c2450" className="pulse" />
            <circle cx="200" cy="80" r="20" fill="#2c3c5450" className="floating-slow" />
            <circle cx="450" cy="60" r="15" fill="#942c2450" className="floating-reverse-slow" />
            
            {/* More visible connecting lines */}
            <path d="M 220 80 L 370 130" stroke="#2c3c5460" strokeWidth="3" className="draw-line" />
            <path d="M 400 320 L 300 220" stroke="#942c2460" strokeWidth="3" className="draw-line-delayed" />
            <path d="M 200 250 L 300 320" stroke="#2c3c5460" strokeWidth="3" className="draw-line-delayed-2" />
            
            {/* Enhanced animated digital particles */}
            <circle cx="370" cy="130" r="6" fill="#2c3c5480" className="particle particle-1" />
            <circle cx="300" cy="220" r="6" fill="#942c2480" className="particle particle-2" />
            <circle cx="300" cy="320" r="6" fill="#2c3c5480" className="particle particle-3" />
            
            {/* Additional decorative elements for visual interest */}
            <polygon points="420,180 450,200 420,220" fill="#942c2440" className="floating-shape" />
            <polygon points="180,120 210,100 210,140" fill="#2c3c5440" className="floating-shape-reverse" />
            <path d="M250,300 C270,320 290,300 310,320" stroke="#942c2450" strokeWidth="3" fill="none" className="wave-line" />
          </svg>
        </div>
        
        <div className="hero-shape"></div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="services-bg-gradient"></div>
        
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <div className="section-subtitle">What We Offer</div>
            <h2>Our Services</h2>
            <p>Transforming ideas into exceptional digital experiences</p>
          </div>

          <div className="services-grid">
            <div className="service-card" data-aos="fade-up" data-aos-delay="100">
              <div className="service-icon-wrapper">
                <FaDesktop className="service-icon" />
              </div>
              <h3>Responsive Web Design</h3>
              <p>Modern, responsive websites optimized for all devices with packages for personal profiles, e-commerce, and business solutions.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
            
            <div className="service-card" data-aos="fade-up" data-aos-delay="200">
              <div className="service-icon-wrapper">
                <FaPencilRuler className="service-icon" />
              </div>
              <h3>Graphic Design</h3>
              <p>Eye-catching logos, flyers, marketing materials, software wireframes and UI/UX mockups to elevate your brand identity.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
            
            <div className="service-card" data-aos="fade-up" data-aos-delay="300">
              <div className="service-icon-wrapper">
                <FaGamepad className="service-icon" />
              </div>
              <h3>Game Development</h3>
              <p>Engaging educational games, sports simulations, and action-packed experiences for various platforms.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
            
            <div className="service-card" data-aos="fade-up" data-aos-delay="400">
              <div className="service-icon-wrapper">
                <FaMobile className="service-icon" />
              </div>
              <h3>Mobile & Desktop Apps</h3>
              <p>Cross-platform applications that provide seamless experiences across Android, iOS, Windows, macOS, and Linux.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
            
            <div className="service-card" data-aos="fade-up" data-aos-delay="500">
              <div className="service-icon-wrapper">
                <FaChalkboardTeacher className="service-icon" />
              </div>
              <h3>Coding Tutorials</h3>
              <p>Comprehensive programming courses at beginner, intermediate, and advanced levels for various languages and frameworks.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
            
            <div className="service-card" data-aos="fade-up" data-aos-delay="600">
              <div className="service-icon-wrapper">
                <FaCode className="service-icon" />
              </div>
              <h3>Custom Solutions</h3>
              <p>Tailored software solutions designed to address your specific business challenges and requirements.</p>
              <NavLink to="/services" className="service-link">Learn More <FaArrowRight className="btn-icon" /></NavLink>
            </div>
          </div>
        </div>

        <div className="moving-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path className="wave-path" fill="#2c3c5420" d="M0,192L48,202.7C96,213,192,235,288,229.3C384,224,480,192,576,181.3C672,171,768,181,864,181.3C960,181,1056,171,1152,176C1248,181,1344,203,1392,213.3L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-title" data-aos="fade-up">
            <div className="section-subtitle">Why Choose Us</div>
            <h2>Our Key Features</h2>
            <p>What sets us apart from the competition</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card" data-aos="fade-up" data-aos-delay="100">
              <div className="feature-icon">
                <FaLaptopCode />
              </div>
              <h3>Modern Technology</h3>
              <p>We use the latest technologies and frameworks to deliver cutting-edge solutions.</p>
            </div>
            
            <div className="feature-card" data-aos="fade-up" data-aos-delay="200">
              <div className="feature-icon">
                <FaBolt />
              </div>
              <h3>Fast Development</h3>
              <p>Our optimized workflow ensures quick turnaround times without compromising quality.</p>
            </div>
            
            <div className="feature-card" data-aos="fade-up" data-aos-delay="300">
              <div className="feature-icon">
                <FaShieldAlt />
              </div>
              <h3>Security Focus</h3>
              <p>We prioritize security in all our projects, protecting your data and your users.</p>
            </div>
            
            <div className="feature-card" data-aos="fade-up" data-aos-delay="400">
              <div className="feature-icon">
                <FaCogs />
              </div>
              <h3>Customized Solutions</h3>
              <p>Every project is tailored to meet your specific business needs and objectives.</p>
            </div>
            
            <div className="feature-card" data-aos="fade-up" data-aos-delay="500">
              <div className="feature-icon">
                <FaRegLightbulb />
              </div>
              <h3>Creative Approach</h3>
              <p>We bring innovative ideas and creative solutions to every challenge we face.</p>
            </div>
            
            <div className="feature-card" data-aos="fade-up" data-aos-delay="600">
              <div className="feature-icon">
                <FaUserFriends />
              </div>
              <h3>Dedicated Support</h3>
              <p>Our team provides ongoing support and maintenance to ensure your project's success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <div className="section-subtitle">Start Your Project</div>
            <h2>Ready to Transform Your Ideas?</h2>
            <p>Let's work together to bring your vision to life with our expertise and innovative solutions.</p>
            <NavLink to="/contact" className="btn-primary glow-effect">
              Get in Touch <FaPaperPlane className="btn-icon" />
            </NavLink>
          </div>
          
          <div className="cta-illustration" data-aos="fade-left" data-aos-delay="200">
            <div className="cta-stars">
              <FaStar className="cta-star star-1" />
              <FaStar className="cta-star star-2" />
              <FaStar className="cta-star star-3" />
              <FaStar className="cta-star star-4" />
              <FaStar className="cta-star star-5" />
            </div>
            <div className="cta-rocket">
              <FaRocket />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
